import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * At the minimum quantity the "minus" control turns into a remove (trash) action:
 * instead of decrementing to an invalid quantity, clicking it asks the parent cart
 * drawer to open its remove-confirmation UI (via a bubbling `cart-line:request-remove`
 * event). The `data-at-min` attribute drives the trash⇄minus icon swap in the stylesheet.
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits.
   * Cart buttons are always managed client-side, never server-disabled.
   * The minus button stays enabled at the minimum so it can trigger removal;
   * `data-at-min` flags that state for the trash-icon swap.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    this.toggleAttribute('data-at-min', value <= min);
    minusButton.disabled = false;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * At the minimum quantity, the minus control requests removal instead of
   * decrementing. Otherwise it behaves normally.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { min, value } = this.getCurrentValues();
    if (value <= min) {
      const cartLine = Number(this.refs.quantityInput?.dataset.cartLine) || undefined;
      this.dispatchEvent(
        new CustomEvent('cart-line:request-remove', {
          bubbles: true,
          composed: true,
          detail: { cartLine },
        })
      );
      return;
    }

    super.decreaseQuantity(event);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
